import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { usePrevious } from 'lib/core/hooks'
import { Toast } from 'lib/ui'
import { AddToCartModal } from 'lib/modules/catalog'
import { getAddProduct } from 'lib/core/data/quote/addProduct/selectors'

interface Props {
  sku: string
  showToasts: boolean
  showModal?: boolean
  onAddToCartSuccess?: () => void
  onAddToCartFailure?: () => void
  productName?: string
  productImage?: string
  modalExtraContent?: JSX.Element | JSX.Element[]
  continueShoppingLink?: string
}

export const ProductNotification = ({
  sku,
  showToasts,
  showModal,
  onAddToCartSuccess,
  onAddToCartFailure,
  productName,
  productImage,
  modalExtraContent,
  continueShoppingLink
}: Props) => {
  const getAddProductSelector = useSelector(getAddProduct(sku))
  const [successModalVisible, setSuccessModalVisible] = useState(false)

  const {
    isFetching,
    isSuccess,
    isFailure,
    errorCode
  } = getAddProductSelector

  const prevIsFetching = usePrevious(isFetching)

  useEffect(() => {
    const isFetchingChanged = !!prevIsFetching && !isFetching

    if (isFetchingChanged && isSuccess) {
      onAddToCartSuccess?.()
      showToasts && Toast.toastSuccess({ messageId: 'catalog.product.add.success' })
      showModal && setSuccessModalVisible(true)
    }

    if (isFetchingChanged && isFailure) {
      const messageId = errorCode ? `catalog.product.add.failure.${errorCode}` : 'catalog.product.add.failure'
      onAddToCartFailure?.()
      showToasts && Toast.toastFailure({ messageId })
    }
  }, [
    isFetching,
    isSuccess,
    isFailure,
    prevIsFetching,
    errorCode,
    showToasts,
    onAddToCartSuccess,
    onAddToCartFailure,
    showModal
  ])

  if (showModal) {
    return (
      <AddToCartModal
        isModalVisible={successModalVisible}
        setIsModalVisible={setSuccessModalVisible}
        productName={productName}
        productImage={productImage}
        sku={sku}
        continueShoppingLink={continueShoppingLink}
      >
        {modalExtraContent}
      </AddToCartModal>
    )
  }

  return null
}
